/* tslint:disable:typedef-whitespace no-trailing-whitespace no-inferrable-types import-spacing */

import { ElementRef }         from '@angular/core';
import { first }              from "rxjs/operators";
import { v4 as uuidv4 }       from 'uuid';

import { ResizeObserver }       from "resize-observer";

import { Dialog }             from './dialog';
import { Logger }             from '@/util/logger';
import { ServiceManager }     from '@/services/service.manager';
import { IValidatorCallBack } from './i.validator.call.back';
import { IOkCallBack }        from './i.ok.call.back';
import { ProjectFactory }     from '@/models/project.factory';
import { Project }            from '@/models/project';
import { ProjectSummary }     from '@/models/project/project.summary';
import { errorAsText }        from '@/util/util';

import * as $                 from 'jquery';

import {
  GridApi,
  GridReadyEvent,
  GridOptions,
  SelectionChangedEvent,
  RowNode,
  RowDoubleClickedEvent,
  ColDef,
  ValueFormatterParams,
  IRowNode,
  createGrid
} from 'ag-grid-community';

import "ag-grid-enterprise";

import { DateTime } from "luxon";


/**
 *
 * @class OpenProjectDialog
 *
 * @brief dialog for picking (to open) an existing project
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class OpenProjectDialog extends Dialog {

  /** the API for our AGGrid */
  private api: GridApi|null                  = null;

  /** the dom that we inserted AGGrid into */
  private dom: HTMLElement|null              = null;

  /** the #ID of the <div> we inject into */
  private id: string                         = '';

  /** the dialog content area (we can set as we like) */
  private body: JQuery<HTMLElement>|null     = null;

  /** the current selection (if any) */
  private projectName: string                = '';
  private projectKey: string                 = '';

  private canResize: boolean                 = false;

  /** standard constructor, allow for partial alert to be given */
  constructor(
    host: ElementRef,
    private services: ServiceManager) {

    super(host);

    let opd: OpenProjectDialog = this;

    opd.title = 'Open Project';
    opd.icon  = '<i class="far fa-folder-open"></i>';

    Logger.debug('[OpenProjectDialog] setting up...');

    opd.body  = $(opd.host).find('.modal-body');

    $(opd.body).html(`<p>Loading projects...</p>`);
  }

  /** dialog has been opened */
  public open(validator?: IValidatorCallBack, success?: IOkCallBack, options?: any) {

    let opd: OpenProjectDialog = this;
    opd.id                     = `open-project-dialog-agrid_${uuidv4()}`;

    Logger.debug('[OpenProjectDialog] opening...');

    /* ask the project service for the list of project summaries */

    this.services.projects.myProjects()
      .pipe(first())
      .subscribe({
        next: (listing: any): void => {

          if (!listing || typeof listing['data'] == 'undefined') {

            Logger.error(`[OpenProjectDialog] empty project list? `, listing);

            return ;
          }

          listing = listing.data.map((x: ProjectSummary) => new ProjectSummary(x));

          const html: string = `
<div class="open-project-dialog-agrid-wrapper"  id="open-project-dialog-agrid-wrapper-${opd.id}"  style="min-height: 250px;  height: 250px; overflow: scroll;">

  <div id="${opd.id}" class="open-project-dialog-agrid ag-theme-balham-dark" style="width: 100%; height: 100%; overflow: scroll; min-height: 250px;">

   </div>

</div>
    `;

          /* set/reset the wrapper where we will inject a new AGGrid... */

          $(opd.body).html(html);

          opd.disable();

          /* now we have to wait for the DOM to be there, then we can inject AG Grid... */

          const checkExist = setInterval(() => {

            opd.dom = (document.getElementById(opd.id) as HTMLElement) ?? null;

            if (!opd.dom) {

              /* It's not there yet */

              return ;
            }

            clearInterval(checkExist);

            /* Browser has created the <div> for us...time to inject AGGrid ... */

            opd.inject(listing);

          }, 100);

          /* end of filling the dialog body */

        },
        error: (err: any): void => {

          Logger.error('[OpenProjectDialog] could not fetch projects: ', errorAsText(err));

        }
      });

  }

  /**
   * Dialog is ready to have AG Grid inserted...
   *
   * @param projects the project to let the user pick from
   *
   */
  public inject(projects: ProjectSummary[]): void {

    let opd: OpenProjectDialog = this;

    /*
     * before setting up AGGrid, it needs a fixed height container to work within, so
     * we give it that inside the dialog wrapper container, but then what happens when
     * you resize the dialog?  nothing. :( dialog gets bigger, but AGGrid stays the same
     * height :(
     *
     * So we have to add an observer to correct the height of the AGGrid container
     * if the user resizes the surrounding dialog.
     *
     */

    opd.canResize                = false;
    const wrapperId: string      = `open-project-dialog-agrid-wrapper-${opd.id}`;
    const innerId: string        = `${opd.id}`;

    /*
     * set the initial height to whatever the model body has, it can change
     * because our dialogs are all "hot swapped" into a common dialog window's
     * content.  So when we come up it may be after we've already been up,
     * or some other dialog has already been up, so the initial height may not
     * be the default initial height.
     *
     */

    $(`#${wrapperId}`).height($(`#${wrapperId}`).parent().height() - 32);

    /*
     * watch for updates, but only from the user, not from internal attempts to
     * extend the height.
     *
     */

    const observer = new ResizeObserver(entries => {

      entries.forEach(entry => {

        /* update the fixed height of the AGGrid wrapper  */

        if (opd.canResize) {

          /* modal body has a 16px padding so (vertically) get 32px less space */

          $(`#${wrapperId}`).height(entry.contentRect.height - 32);
        }

      });
    });

    let el: Element|null = document.querySelector(`#${wrapperId}`).parentElement;

    if (el) {

      observer.observe(el);

    } else {

      Logger.error(`[OpenProjectDialog(${opd.id})] - no wrapper element??`);

    }

    /* setup AGGrid ... */

    Logger.debug(`[OpenProjectDialog] inject - setting up AGGrid, DOM: `, opd.dom);
    Logger.debug('[OpenProjectDialog] inject - my projects: ', projects);

    const rowData: any[]    = [
    ];

    projects.forEach((x: ProjectSummary): void => {

      const row = {
        name:        x.name,
        modified:    x.modified,
        description: x.description,
        key:         x.key,
        oid:         x.oid
      };

      rowData.push(row);

    });

    const columnDefs: ColDef[] = [
      {
        headerName:        'Name',
        field:             'name',
        width:             200,
        sortable:          true,
        sortingOrder:      ['asc', 'desc'],
        checkboxSelection: true,
        comparator:        (valueA: string, valueB: string): number => {

          if (valueA == valueB) {

            return 0;
          }

          return (valueA > valueB) ? 1 : -1;
        }
      },
      {
        headerName:     'Modified',
        field:          'modified',
        width:          250,
        valueFormatter: (params: ValueFormatterParams): string => {

          /* we expect modification time to be an instance of Luxon (DateTime) */

          if (!(params.value instanceof DateTime)) {

            return '####';
          }

          let dateTime: DateTime = params.value as DateTime;

          return dateTime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
        },
        comparator:        (valueA: string, valueB: string, nodeA: RowNode, nodeB: RowNode): number => {

          if (!nodeA || !nodeB) {

            return 0;
          }

          let a: DateTime = nodeA.data.modified;
          let b: DateTime = nodeB.data.modified;

          /* we expect modification time to be an instance of Luxon (DateTime) */

          if (a.toSeconds() == b.toSeconds()) {

            return 0;
          }

          return (a.toSeconds() < b.toSeconds()) ? 1 : -1;
        }
      },
      {
        headerName: 'Description',
        field:      'description',
        width:      150,
      },
      {
        headerName: 'Path',
        field:      'key',
        width:      250,
      },
      {
        headerName: 'OID',
        field:      'oid',
        width:      80,
      }
    ];

    const gridOptions: GridOptions = {

      columnDefs,

      rowData,

      onGridReady: (params: GridReadyEvent) => {

        opd.api       = params.api;
        opd.canResize = true;

        Logger.debug(`[OpenProjectDialog] inject - Grid READY!`);

        /* by default sort on the project name */

        opd.api.applyColumnState({
          state:        [{ colId: 'name', sort: 'asc'}],
          defaultState: {sort: null}
        });

      },

      onRowDoubleClicked(event: RowDoubleClickedEvent): void {

        let rows: IRowNode[] = event.api.getSelectedNodes();

        /* set the selection */

        opd.projectName = rows[0].data.name;
        opd.projectKey  = rows[0].data.key;

        Logger.debug(`[OpenProjectDialog] inject - new project selection: project=${opd.projectName}, key=${opd.projectKey}`);

        /* double click also counts as clicking on the 'Ok' button */

        opd.validate();
      },

      onSelectionChanged(event: SelectionChangedEvent): void {

        let rows: IRowNode[] = event.api.getSelectedNodes();

        if (rows.length == 0) {

          opd.projectName = '';
          opd.projectKey  = '';

          Logger.debug(`[OpenProjectDialog] inject - new project selection: project=${opd.projectName}, key=${opd.projectKey}`);

          opd.disable();

          return ;
        }

        /* set the selection */

        opd.projectName = rows[0].data.name;
        opd.projectKey  = rows[0].data.key;

        opd.enable();

        Logger.debug(`[OpenProjectDialog] inject - new project selection: project=${opd.projectName}, key=${opd.projectKey}`);
      },

      defaultColDef: {
        resizable: true,
        editable: false,
        sortable: true,
        filter:   true
      },

      rowSelection: 'single',

      domLayout: 'autoHeight'
    }

    opd.api = createGrid(opd.dom, gridOptions);
  }

  /** dialog is closing */
  public close(): void {

    let opd: OpenProjectDialog = this;

    $(opd.body).html(`<p>Loading projects...</p>`);

    Logger.debug('[OpenProjectDialog] closing...');

  }

  /** dialog was accepted */
  public ok(): void {

    let opd: OpenProjectDialog = this;

    $(opd.body).html(`<p>Loading projects...</p>`);

    Logger.debug(`[OpenProjectDialog] accepted key=${opd.projectKey}`);

    /* one they pick a project, we just invoke the project service to open that project... */

    this.services.blobber.contents(this.projectKey)
      .pipe(first())
      .subscribe({
          next: (contents: any): void => {

            const project: Project = ProjectFactory.fromJSON(contents as string) as Project;

            Logger.debug('[OpenProjectDialog] opening project: ', project);

            this.services.projects.openProject(project);

          },
          error: (err: any): void => {

            Logger.error('[OpenProjectDialog] can not fetch project data: ', errorAsText(err));
          }
        }
      );
  }

  public validate(): void {

    let opd: OpenProjectDialog       = this;

    if (opd.projectKey == '') {

      /* no selection yet */

      return ;
    }

    /* click the ok button, we're done! */

    const ok: JQuery<HTMLElement> = $(opd.host).find('.modal-ok-button');

    $(ok).trigger('click');
  }

  disable() {

    let opd: OpenProjectDialog    = this;
    const ok: JQuery<HTMLElement> = $(opd.host).find('.modal-ok-button');

    $(ok).attr('disabled', 'true');
    $(ok).addClass('disabled');
  }

  enable() {

    let opd: OpenProjectDialog    = this;
    const ok: JQuery<HTMLElement> = $(opd.host).find('.modal-ok-button');

    $(ok).removeAttr('disabled');
    $(ok).removeClass('disabled');

  }
}


